/**
 * NoJira 어드민 라우트 → 레거시 page path(pageKey) 매핑.
 *
 * 페이지 권한(pages / role_page_permissions)은 레거시 식별자(admin.contact,
 * adminSI.invList 등)를 키로 쓴다. 반면 NoJira 라우터는 /contact, /finance/invoices
 * 같은 경로를 쓴다. 메뉴/라우트를 pagePermission(canShow)로 게이팅하려면 이 매핑이 필요.
 *
 * 매핑에 없는 라우트(= 이식 후 신규 페이지: /readiness, /finance/disbursements 등)는
 * pageKey=undefined 로 취급 → canShowPage(undefined)=true(항상 노출). 신규 기능을
 * 실수로 숨기지 않기 위함이며, Admin 은 어차피 전부 bypass 한다.
 */
export const ROUTE_PAGE_KEY: Record<string, string> = {
  '/contact': 'admin.contact',
  '/email-templates': 'admin.emailTemplates',
  '/users': 'admin.allUsers',
  // 신청인 화면은 계정관리(/users)와 키를 나눈다 — 마이그레이션 0299.
  '/applicants': 'admin.applicants',
  '/crm': 'admin.checkList',
  '/permissions': 'admin.pagePermission',
  '/service-requests': 'admin.srRequests',
  // Cases (도메인 대표 pageKey = admin.caseI140; 세부 /cases/i140 등은 아래에서 개별 매핑)
  // 목록·검색·관심목록은 단계 화면과 키를 나눈다(마이그레이션 0452).
  // 예전에는 셋 다 admin.caseI140 이라, 제출 단계 화면만 닫으려 하면 목록까지
  // 닫혀 외부 변호사에게 볼 화면이 하나도 안 남았다.
  '/cases': 'admin.caseList',
  '/cases/applicant': 'admin.caseList',
  '/watching': 'admin.caseList',
  // 0380 — 명부와 문서함을 가른다. agent_admin 은 문서함만 본다.
  '/agent-documents': 'admin.agentDoc',
  // Application
  '/applicants/current': 'admin.currApply',
  '/applicants/applications': 'admin.jobApply',
  '/applicants/by-campaign': 'admin.cmpApply',
  '/cases/eta': 'admin.ETAApply',
  '/cases/i140': 'admin.caseI140',
  '/cases/i485': 'admin.caseI485',
  '/cases/ds260': 'admin.case260',
  '/cases/i824': 'admin.caseI824',
  '/cases/withdraw': 'admin.withdraw',
  // Case Reports
  '/reports/cases/by-agent': 'admin.agentReport',
  '/reports/cases/by-employer': 'admin.empCases',
  '/reports/cases/by-attorney': 'admin.attnyCases',
  '/reports/cases/by-status': 'admin.caseReport',
  '/reports/cases/monthly': 'admin.mChart',
  '/reports/employers': 'admin.empReport',
  // REPORT — Super Admin 전용. pages 에 등록하지 않아 권한 화면에서 남에게 켤 수 없다
  // (canShowPage 는 Super Admin 만 통과, 서버도 Super Admin 역할로 막는다).
  '/super-reports/withdrawn': 'admin.superReportWithdrawn',
  '/super-reports/campaigns': 'admin.superReportCampaigns',
  '/super-reports/refunds': 'admin.superReportRefunds',
  // CRM
  '/employers': 'admin.empList',
  '/agents': 'admin.agents',
  '/legal-advisors': 'admin.attorneys',
  '/campaigns': 'admin.campaign',
  '/campaigns/calendar': 'admin.cmpCalendar',
  // Questionnaires
  '/questionnaires/applicant': 'admin.gnrQstnr',
  '/questionnaires/employer': 'admin.questionnaire',
  // Content / Notifications
  '/blog': 'admin.blogMgmt',
  '/notifications/messages': 'admin.message',
  // Relocation (정착 서비스) — legacy 없는 신규 페이지라 RLC 소스 키를 쓴다.
  '/relocation/customers': 'admin.rlcCustomers',
  // Board 는 Customers 와 분리(0527) — Legal 팀은 Customers 만 읽는다.
  '/relocation/followups': 'admin.rlcBoard',
  '/relocation/bulk-email': 'admin.rlcBulkEmail',
  '/relocation/field-staff': 'admin.rlcFieldStaff',
  '/relocation/schedules': 'admin.rlcSchedules',
  '/relocation/dashboard': 'admin.rlcDashboard',
  '/relocation/topups': 'admin.rlcCredits',
  '/relocation/reviews': 'admin.rlcFieldStaff',
  '/relocation/dispatch': 'admin.rlcDispatch',
  '/relocation/active': 'admin.rlcActive',
  '/relocation/anomalies': 'admin.rlcAnomalies',
  '/relocation/rate-notices': 'admin.rlcRateNotices',
  '/relocation/hourly-rates': 'admin.rlcHourlyRates',
  '/relocation/payouts': 'admin.rlcPayouts',
  '/relocation/extra-claims': 'admin.rlcExtraClaims',
  '/relocation/expense-claims': 'admin.rlcExpenseClaims',
  '/relocation/service-areas': 'admin.rlcServiceAreas',
  '/relocation/codes': 'admin.rlcCodes',
  '/relocation/expense-types': 'admin.rlcExpenseTypes',
  // 항목표는 고객별 체크리스트와 같은 키를 쓴다 — 새 권한 키를 만들면
  // 마이그레이션 전까지 아무에게도 안 보인다.
  '/relocation/checklist-items': 'admin.rlcChecklist',
  '/relocation/lms': 'admin.rlcLmsCatalog',
  '/relocation/lms/enrollments': 'admin.rlcLmsEnrollments',
  '/relocation/lms/access': 'admin.rlcLmsAccess',
  '/relocation/lms/progress': 'admin.rlcLmsProgress',
  '/relocation/topup-packages': 'admin.rlcTopup',
  '/relocation/terms': 'admin.rlcTerms',
  // Finance
  '/finance/invoices': 'adminSI.invList',
  '/finance/payments': 'adminSI.payUserList',
  '/finance/new-contracts': 'admin.newContracts',
  '/finance/payments/upload': 'adminSI.pmtUpload',
  '/finance/payment-requests': 'adminSI.userReqPmt',
  '/finance/promotions': 'admin.promotion',
  '/finance/prepaid': 'adminSI.prePaidList',
  '/finance/cashflow': 'adminSI.cashflow',
  '/finance/balance-report': 'adminSI.balanceReport',
  // Commission
  '/finance/commissions/request-commission/generate': 'admin.cmsRequest',
  '/finance/commissions/confirm': 'admin.cmsConfirm',
  '/finance/commissions/history': 'admin.cmsHistory',
  '/finance/commissions/types': 'admin.cmsType',
  '/finance/commissions/agent-info': 'admin.agentCms',
  '/finance/commissions/pending-approvals': 'admin.setConfirm',

  // NoJira 신규 화면 — 마이그레이션 0298 이 pages(legacy_source='NJR') 로 만든 키.
  // 이 매핑이 있어야 라우터 가드가 canShow 로 판정할 수 있다.
  '/audit-log': 'admin.auditLog',
  '/chatbot': 'admin.chatbot',
  '/dashboard': 'admin.dashboard',
  '/deadline/ad-to-eta': 'admin.deadlineAdToEta',
  '/deadline/lc-to-i140': 'admin.deadlineLcToI140',
  '/deadline/rfe': 'admin.deadlineRfe',
  '/faqs': 'admin.faqs',
  '/faqs/new': 'admin.faqsNew',
  '/faqs/suggestions': 'admin.faqsSuggestions',
  '/finance/ach-behalf': 'admin.financeAchBehalf',
  '/finance/commissions': 'admin.financeCommissions',
  '/finance/commissions/by-agent': 'admin.financeCommissionsByAgent',
  '/finance/commissions/calc': 'admin.financeCommissionsCalc',
  '/finance/commissions/contracts': 'admin.financeCommissionsContracts',
  '/finance/commissions/rate-cards': 'admin.financeCommissionsRateCards',
  '/finance/commissions/request-commission': 'admin.financeCommissionsRequestCommission',
  '/finance/cost-management': 'admin.financeCostManagement',
  '/finance/disbursement-requests': 'admin.financeDisbursementRequests',
  '/finance/field-staff-payouts': 'admin.financeFieldStaffPayouts',
  '/finance/late-fees': 'admin.financeLateFees',
  '/finance/receipts': 'admin.financeReceipts',
  '/finance/withdraw-refund': 'admin.financeWithdrawRefund',
  '/generate': 'admin.generate',
  '/invitations': 'admin.invitations',
  '/jobs': 'admin.jobs',
  '/notifications/notices': 'admin.notificationsNotices',
  '/notifications/notices/new': 'admin.notificationsNoticesNew',
  '/notifications/push': 'admin.notificationsPush',
  '/readiness': 'admin.readiness',
  '/settings/document-templates': 'admin.settingsDocumentTemplates',
  '/settings/help-articles': 'admin.settingsHelpArticles',
  // 페이지 권한으로 할당(0487). 기본은 Super Admin 만.
  '/settings/notifications': 'admin.settingsNotifications',
  '/settings/mobile-versions': 'admin.settingsMobileVersions',
  '/settings/help-articles/new': 'admin.settingsHelpArticlesNew',
  '/settings/lookup-codes': 'admin.settingsLookupCodes',
  // 사이드바는 정확 일치로 키를 찾으므로 하위 화면도 명시한다.
  '/applicants/anomalies': 'admin.applicantAnomalies',
  '/blog/categories': 'admin.blogMgmt',
  '/crm/contract-requests': 'admin.contractRequests',
};

/**
 * agent_admin — agent 이면서 제한적 admin 접근이 필요한 역할(예: 블로그 등록).
 * 이 역할만 가진(다른 스태프 역할 없는) 계정은 아래 prefix 페이지만 접근 가능.
 * "신규 페이지 항상 노출" 기본값이 제한역할에 새는 걸 막기 위해 명시적 화이트리스트.
 */
export const AGENT_ADMIN_ROLE = 'agent_admin';
// 열람 허용 영역: 운영 데이터 전반(지원자·케이스·리포트·서비스요청·알림·
// 블로그[+등록]·연락). CRM 그룹에서는 Agent docs·Employers·Campaigns 만 —
// Agent 명부(/agents)는 제외한다.
// /crm(리드 관리)과 /questionnaires 는 뺐다(0443) — 볼 필요가 없는 화면이다.
// 질문지 read 권한은 남겨 둔다: 신청인 상세의 Background/Applications 탭이 같은
// 키로 가려져 있어, 읽기까지 끄면 그 탭이 403 이 된다.
// 재무/커미션/정산, Access 계정관리(users/permissions),
// 대시보드/보안감사 페이지는 제외 — 백엔드도 이 영역만 read-only 로 열려 있음.
export const AGENT_ADMIN_ALLOWED_PREFIXES = [
  '/applicants', '/cases', '/watching', '/deadline', '/reports',
  '/employers', '/agent-documents', '/campaigns',
  '/service-requests',
  '/blog', '/notifications', '/contact',
];

/**
 * 본인 것만 담긴 화면 — 로그인만 하면 연다.
 *
 * 받은 편지함이 그렇다. 캠페인 담당자·케이스 담당자에게 인앱 알림을 보내면서
 * 정작 그 편지함은 admin.message(다른 사람 메시지까지 보는 조정 권한)로
 * 잠가 둬, 알림을 받은 사람이 열면 권한 오류가 났다. 편지함 안의 조정 탭은
 * 그 화면에서 따로 가린다.
 */
export const PERSONAL_PAGES = ['/notifications/messages'];

/**
 * 본인 화면인가 — 페이지 권한 게이트를 건너뛴다(로그인은 여전히 필요).
 *
 * 정확히 일치할 때만이다. 접두로 재면 /notifications/messages/broadcast·
 * /compose(발송 화면)까지 함께 열린다 — 그건 조정 권한이 있는 사람 것이다.
 */
export function isPersonalPage(path: string): boolean {
  // 받은 메시지 한 통(/notifications/messages/<uuid>)도 본인 것이다 — 목록만 열고 상세를
  // 막아 두면 알림을 받은 직원이 본문·Open 링크를 볼 수 없었다. 상세 화면은 조정 권한이
  // 없으면 본인 받은 편지함 API(getMyMessage)로만 읽는다. broadcast·compose 는 여전히 막는다.
  return PERSONAL_PAGES.includes(path) || /^\/notifications\/messages\/[0-9a-f-]{36}$/i.test(path);
}

/** agent_admin 제한역할이 접근 가능한 경로인가(prefix 매칭). */
export function isAllowedForAgentAdmin(path: string): boolean {
  return AGENT_ADMIN_ALLOWED_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
}

/**
 * 라우트 경로에 해당하는 pageKey 를 찾는다(라우터 가드용). 상세 라우트
 * (/finance/invoices/:id) 는 가장 긴 세그먼트-접두 매칭으로 목록 페이지 키에 붙인다.
 * 매핑 없으면 undefined → 게이팅 안 함(신규 페이지).
 */
export function pageKeyForRoute(path: string): string | undefined {
  let bestKey: string | undefined;
  for (const key of Object.keys(ROUTE_PAGE_KEY)) {
    if (path === key || path.startsWith(key + '/')) {
      if (!bestKey || key.length > bestKey.length) bestKey = key;
    }
  }
  return bestKey ? ROUTE_PAGE_KEY[bestKey] : undefined;
}
